import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {PhoneNumber} from './phone-number.service';
import {isNullOrUndefined} from 'util';

@Injectable()
export class CallLogService {

  constructor(private httpClient: HttpClient) {
  }

  getLastByUsername(username: string, customerId: string, since: number): Observable<Array<CallLog>> {
    return this.httpClient
      .get<Array<CallLogResponse>>(`/api/call_event/v1/cdr/find/username/${username}/customerId/${customerId}` +
        `/since/${since}`)
      .map((res: Array<CallLogResponse>) => res.map((callLog: CallLogResponse) => CallLog.fromJson(callLog)))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
    // return this.httpClient.get<Array<CallLog>>(`/api/call_event/v1/cdr/find/username/${username}/customerId/${customerId}`);
  }

  getLastByUserId(userId: string, since: number): Observable<Array<CallLog>> {
    return this.httpClient
      .get<Array<CallLogResponse>>(`/api/call_event/v1/cdr/find/user_id/${userId}` +
        `/since/${since}`)
      .map((res: Array<CallLogResponse>) => res.map((callLog: CallLogResponse) => CallLog.fromJson(callLog)))
      .catch((response: HttpErrorResponse) => Observable.throw(response.error.message || 'Server error'))
    // return this.httpClient.get<Array<CallLog>>(`/api/call_event/v1/cdr/find/username/${username}/customerId/${customerId}`);
  }
}

export class CallLogUtils {
  static getPrettyCost(cost: number): string {
    return cost.toFixed(2);
  }

  static convertToHMS(secs: number): string {
    const seconds = secs % 60;
    const minutes = (Math.floor(secs / 60) % 60);
    const hours = Math.floor(secs / 3600);
    return `${hours}:${CallLogUtils.padZeros(minutes)}:${CallLogUtils.padZeros(seconds)}`;
  }

  static padZeros(n: number): string {
    return ('0' + n).slice(-2);
  }

  static extractBackEndDate(json: any): number {
    let callDate: number;
    if (json.length === 6) {
      callDate = Date.UTC(json[0], json[1] - 1, json[2], json[3], json[4], json[5] || 0);
    } else if (json.length === 5) {
      callDate = Date.UTC(json[0], json[1] - 1, json[2], json[3], json[4], 0);
    } else if (json.length === 3) {
      callDate = Date.UTC(json[0], json[1] - 1, json[2], 0, 0, 0 || 0);
    }
    const tzOffset = (new Date()).getTimezoneOffset();
    return (callDate + tzOffset * 60 * 1000);
  }

  static extractToMapOfCallTariffTypes(obj: any): Map<string, Map<string, number>> {
    const map = new Map<string, Map<string, number>>();
    Object.keys(obj).forEach(key => {
      const innerMap = new Map<string, number>();
      Object.keys(obj[key]).forEach(innerKey => {
        innerMap.set(innerKey, obj[key][innerKey])
      });
      map.set(key, innerMap);
    });
    return map;
  }
}

export class CallLog implements CallLogResponse {

  static readonly callClassTranslations = {
    'INTERNAL': 'Interna',
    'INTERNATIONAL': 'Internacional',
    'LOCAL': 'Local',
    'LONG_DISTANCE': 'Longa Distância',
    'UNKNOWN': 'Desconhecido',
    'VC1': 'Celular VC1',
    'VC2': 'Celular VC2',
    'VC3': 'Celular VC3',
  };

  static readonly callTypeTranslations = {
    'INTERNAL': 'Interna',
    'INCOMING': 'Entrada',
    'OUTGOING': 'Saída',
    'TRANSIT': 'Trânsito',
    'UNKNOWN': 'Desconhecida'
  };

  static fromJson(json: any): CallLog {
    const object: CallLog = Object.create(CallLog.prototype);
    const sourceNumber: PhoneNumber = Object.create(PhoneNumber.prototype);
    const destinationNumber: PhoneNumber = Object.create(PhoneNumber.prototype);
    Object.assign(object, json);
    Object.assign(sourceNumber, json['sourceNumber']);
    Object.assign(destinationNumber, json['destinationNumber']);
    object.sourceNumber = sourceNumber;
    object.destinationNumber = destinationNumber;
    object.start = CallLogUtils.extractBackEndDate(json['start']);
    return object
  }

  static getPrettyClassName(callClass: string): string {
    return CallLog.getPrettyName(callClass, this.callClassTranslations);
  }

  static getPrettyTypeName(callType: string): string {
    return CallLog.getPrettyName(callType, this.callTypeTranslations);
  }

  static getPrettyName(name: string, translations: any) {
    if (isNullOrUndefined(translations) || isNullOrUndefined(translations[name])) {
      return name;
    } else {
      return translations[name];
    }
  }

  constructor(public id: string, public customerId: string, public uniqueId: string, public linkedId: string,
              public source: string, public sourceNumber: PhoneNumber, public destination: string,
              public destinationNumber: PhoneNumber, public start: number, public ringTime: number,
              public conversationTime: number, public q931: number, public callerIdNumber: string,
              public callerIdName: string, public username: string, public cost = 0, public callType: string,
              public callClass: string, public costCenterId: string, public departmentId: string,
              public costCenterName?: string, public departmentName?: string, public userId?: string) {
  }

  getPrettyDestinationNumber(): string {
    if (this.destinationNumber.extension) {
      return this.destinationNumber.getPrettyLDFormat();
    } else {
      return this.destination;
    }
  }

  getPrettySourceNumber(): string {
    if (this.sourceNumber.extension) {
      return this.sourceNumber.getPrettyLDFormat();
    } else {
      return this.source;
    }
  }

  getConversationHMS(): string {
    return CallLogUtils.convertToHMS(this.conversationTime);
  }

  getRingTimeHMS(): string {
    return CallLogUtils.convertToHMS(this.ringTime);
  }

  getPrettyCallClass(): string {
    return CallLog.getPrettyClassName(this.callClass);
  }

  getPrettyCallType(): string {
    return CallLog.getPrettyTypeName(this.callType);
  }
}

export interface CallLogResponse {
  id: string,
  customerId: string
  uniqueId: string
  linkedId: string
  source: string
  sourceNumber: PhoneNumber
  destination: string
  destinationNumber: PhoneNumber
  start: Object
  ringTime: Number
  conversationTime: Number
  q931: Number
  callerIdNumber: string
  callerIdName: string
  username: string
  cost: number
  costCenterId: string
  departmentId: string
  costCenterName?: string
  departmentName?: string
}



// WEBPACK FOOTER //
// /Users/tabelini/development/jvm/tenacity/pysix/PysixCloud/src/app/call-log.service.ts